<template>
  <div class="add-card-page">
    <QHeader class="bg-primary text-white">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="goBack()" />
        <QToolbarTitle>
          {{ $t("ETAB.PAGES.ADMIN_FOLDER.@ADMIN_FOLDER_STATUS_LABEL") }}
        </QToolbarTitle>
      </QToolbar>
    </QHeader>

    <QPageContainer>
      <QPage padding>
        <IconLabel heavy no-padding :label="pageTitle" icon="fad fa-building-columns" class="q-mb-lg" />
        <template v-if="loading">
          <div class="q-my-md">
            <QSkeleton animation="wave" />
          </div>
          <div class="q-my-md">
            <QSkeleton animation="wave" />
          </div>
        </template>
        <IbanForm
          v-else-if="!loading"
          :readonly="readonly"
          :iban-details="ibanDetails"
          @reference-stripe-instances="referenceStripeInstances"
          @save-iban="saveIban"
        />
        <ExtraExpansionTip
          v-if="!readonly"
          :title="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@STRIPE_LEGAL_INFOS_TITLE')"
          content-class="text-caption"
          class="rounded-borders"
        >
          <span>{{ $t("ETAB.PAGES.PAYMENT.ETAB_IBAN.@STRIPE_LEGAL_INFOS_TEXT") }}</span>
        </ExtraExpansionTip>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script>
  import { mapState } from "pinia";
  import { useProfileStore } from "@etab/stores/profile";
  import { Keyboard } from "@capacitor/keyboard";
  import { StatusBar } from "@core/plugins/mobile";
  import { PaymentService } from "@core/services";
  import { StripeHelpers } from "@core/helpers";
  import { ETAB_ROUTES } from "@etab/config";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { IbanModel, StripeMandateModel } from "@core/models";
  import { STRIPE_PK } from "@core/constants";
  import { ACTION_ADMIN_FOLDER_INIT, GETTER_ADMIN_FOLDER_GET, GETTER_PROFILE_GET_ACCOUNT } from "@etab/stores/types";
  import { paymentMixin } from "@etab/mixins";

  import IbanForm from "@etab/components/payment/IbanForm.component.vue";

  export default {
    components: {
      IbanForm,
    },
    mixins: [paymentMixin],
    beforeRouteLeave(to, from, next) {
      if (this.isMobile) {
        Keyboard.hide();
      }
      next();
    },
    data() {
      return {
        stripeInstance: {},
        ibanStripeElement: {},
        loading: false,
        iban: new IbanModel(),
        stripeMandate: new StripeMandateModel(),
      };
    },
    computed: {
      ...mapState(useProfileStore, {
        etabAdminFolder: GETTER_ADMIN_FOLDER_GET,
        account: GETTER_PROFILE_GET_ACCOUNT,
      }),
      stripeMandateEmail() {
        // if stripe account folder has been completed, use associated email address, otherwise use extracadabra account email as a fallback
        return this.etabAdminFolder.getEmail() || this.account.getEmail();
      },
      pageTitle() {
        return this.readonly
          ? this.$t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_IBAN_LABEL")
          : this.$t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_SET_IBAN");
      },
      readonly() {
        return this.etabAdminFolder.getRegisteredIban();
      },
      ibanDetails() {
        return this.etabAdminFolder.getIbanDetails();
      },
    },
    created() {
      this.getStripeMandate();
      this.stripeInstance = window.Stripe && window.Stripe(STRIPE_PK);
    },
    mounted() {
      StatusBar.setPrimaryBackground();
    },
    methods: {
      referenceStripeInstances({ stripeInstance, ibanStripeElement }) {
        // we need those objects in order to get and use Stripe data
        this.stripeInstance = stripeInstance;
        this.ibanStripeElement = ibanStripeElement;
      },
      goBack() {
        const backUrl = this.$route.query?.url;
        const fallbackUrl = ETAB_ROUTES.userMenu.main.name;
        if (backUrl) {
          this.$router.push({ path: backUrl });
        } else {
          this.$router.push({ name: fallbackUrl });
        }
      },
      async getStripeMandate() {
        try {
          this.$_startGlobalLoading();
          this.loading = true;
          const response = await PaymentService.getClientSecretKey();
          this.stripeMandate = new StripeMandateModel(response);
        } catch (error) {
          // In case we fail to retrieve the secret key, go back and show specific error message
          NotificationsManager.error({ message: this.$t("ERRORS.@STRIPE_SECRET_KEY_FAILURE") });
          this.goBack();
          throw error;
        } finally {
          this.loading = false;
          this.$_stopGlobalLoading();
        }
      },
      async saveIban({ formData }) {
        const profileStore = useProfileStore();
        try {
          // retrieve iban stripe element, account owner and account email
          const iban = this.ibanStripeElement;
          const name = formData.getOwner();
          const email = this.stripeMandateEmail;

          this.$_startGlobalLoading();
          const { error } = await this.stripeInstance.confirmSepaDebitSetup(this.stripeMandate.getClientSecret(), {
            payment_method: {
              sepa_debit: iban,
              billing_details: {
                name,
                email,
              },
            },
          });

          StripeHelpers.errorHandler({ error });
          // refresh store admin folder data
          profileStore[ACTION_ADMIN_FOLDER_INIT]();
          NotificationsManager.success({ message: "SUCCESS.@ETAB_IBAN_SAVED_SUCCESS" });
          this.goBack();
        } catch (error) {
          NotificationsManager.error({ message: error.message });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
    },
  };
</script>
